import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    CButton,
    CForm,
    CFormInput,
    CSpinner,
} from '@coreui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../assets/Images/White_Logo.svg';
import openEyeIcon from '../../assets/Images/eye-open.png';
import closedEyeIcon from '../../assets/Images/eye-closed.png';

// ── Screen states ──
const STATUS = {
    VERIFYING: 'verifying',
    VALID: 'valid',
    INVALID: 'invalid',
    SUCCESS: 'success',
};

const CheckIcon = ({ active }) => (
    <span className={`reset-password__req-icon ${active ? 'is-active' : ''}`}>
        {active ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" width="10" height="10">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
            </svg>
        ) : null}
    </span>
);

const BrandPanel = () => (
    <div className="reset-password__brand">
            <div className="forgot-password__brand-inner">
        <div className="reset-password__logo">
            <img src={logo} alt="Quick Query" />
        </div>
        <h1 className="reset-password__headline">
            Automate your account mapping in minutes
        </h1>
        <p className="reset-password__subtext">
            Configure standard expense categories, run clean multi-factor
            compliance reporting, and synchronize seamlessly to your general
            ledger.
        </p>
        </div>
    </div>
);

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState(STATUS.VERIFYING);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [pwErrors, setPwErrors] = useState({});

    // ── Verify token on mount ──
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setStatus(STATUS.INVALID);
                return;
            }
            try {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/user/verify-reset-token`, { token });
                setStatus(STATUS.VALID);
            } catch (err) {
                console.error('Token verification failed:', err);
                setStatus(STATUS.INVALID);
            }
        };
        verifyToken();
    }, [token]);

    // ── Live security requirements ──
    const hasMinLength = newPassword.length >= 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasDigit = /[0-9]/.test(newPassword);
    const passwordsMatch = confirmPassword.length > 0 && confirmPassword === newPassword;
    const allRequirementsMet = hasMinLength && hasUppercase && hasDigit && passwordsMatch;

    const validatePassword = () => {
        const errs = {};
        if (!newPassword) {
            errs.newPassword = 'New password is required';
        } else if (!hasMinLength || !hasUppercase || !hasDigit) {
            errs.newPassword = 'Password does not meet the requirements below';
        }
        if (confirmPassword !== newPassword) {
            errs.confirmPassword = 'Passwords do not match';
        }
        setPwErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (form.checkValidity() === false || !validatePassword()) {
            setValidated(true);
            event.stopPropagation();
            return;
        }

        setLoading(true);
        setShowError(false);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/user/reset-password`,
                { token, newPassword }
            );

            if (response.data.success) {
                setStatus(STATUS.SUCCESS);
                setTimeout(() => {
                    navigate('/login');
                }, 2500);
            }
        } catch (error) {
            console.error('Reset Password Error:', error);
            setShowError(true);
            setErrorMessage(
                error?.response?.data?.message ||
                'Something went wrong. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    // ── Verifying token ──
    if (status === STATUS.VERIFYING) {
        return (
            <div className="reset-password">
                <BrandPanel />
                <div className="reset-password__panel">
                    <div className="reset-password__card reset-password__card--center">
                        <CSpinner color="primary" style={{ width: 36, height: 36 }} />
                        <p className="reset-password__status-text">Verifying your reset link...</p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Invalid / expired token ──
    if (status === STATUS.INVALID) {
        return (
            <div className="reset-password">
                <BrandPanel />
                <div className="reset-password__panel">
                    <div className="reset-password__card reset-password__card--center">
                        <div className="reset-password__status-icon reset-password__status-icon--error">!</div>
                        <h2 className="reset-password__title">Link invalid or expired</h2>
                        <p className="reset-password__description">
                            This password reset link is no longer valid. Please request a new one.
                        </p>
                        <CButton
                            type="button"
                            className="reset-password__submit"
                            onClick={() => navigate('/forgot-password')}
                        >
                            Request new link
                        </CButton>
                    </div>
                </div>
            </div>
        );
    }

    // ── Success screen ──
    if (status === STATUS.SUCCESS) {
        return (
            <div className="reset-password">
                <BrandPanel />
                <div className="reset-password__panel">
                    <div className="reset-password__card reset-password__card--center">
                        <div className="reset-password__status-icon reset-password__status-icon--success">
                            <svg viewBox="0 0 24 24" fill="none" stroke="#1b7a43" strokeWidth="3" width="22" height="22">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
                            </svg>
                        </div>
                        <h2 className="reset-password__title">Password updated</h2>
                        <p className="reset-password__description">
                            Your credentials have been successfully updated. You can now
                            log into QuickQuery with your new secure password.
                        </p>
                        {/* <CButton
                            type="button"
                            className="reset-password__submit"
                            onClick={() => navigate('/login')}
                        >
                            Continue to login
                        </CButton> */}
                    </div>
                </div>
            </div>
        );
    }

    // ── Valid token → show reset password form ──
    return (
        <div className="reset-password">
            <BrandPanel />
            <div className="reset-password__panel">
                <div className="reset-password__card">
                    <h2 className="reset-password__title">Set new password</h2>
                    <p className="reset-password__description">
                        Your new password must be complex enough to protect your
                        database integrations.
                    </p>

                    {showError && (
                        <div className="reset-password__alert reset-password__alert--error">
                            {errorMessage}
                        </div>
                    )}

                    <CForm noValidate validated={validated} onSubmit={handleSubmit}>
                        <label className="reset-password__label" htmlFor="NewPassword">
                            New Password
                        </label>
                        <div className={`reset-password__field ${pwErrors.newPassword ? 'is-invalid' : ''}`}>
                            <span className="reset-password__field-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                    <rect x="4" y="10" width="16" height="10" rx="2" />
                                    <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                                </svg>
                            </span>
                            <CFormInput
                                id="NewPassword"
                                type={passwordVisible ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={newPassword}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    setPwErrors({ ...pwErrors, newPassword: '' });
                                }}
                                required
                            />
                            <button
                                type="button"
                                className="reset-password__field-toggle"
                                onClick={() => setPasswordVisible((p) => !p)}
                            >
                                <img
                                    src={passwordVisible ? openEyeIcon : closedEyeIcon}
                                    alt=""
                                    width="20"
                                />
                            </button>
                        </div>
                        {pwErrors.newPassword && (
                            <p className="reset-password__field-error">{pwErrors.newPassword}</p>
                        )}

                        <label className="reset-password__label" htmlFor="ConfirmPassword">
                            Confirm New Password
                        </label>
                        <div className={`reset-password__field ${pwErrors.confirmPassword ? 'is-invalid' : ''}`}>
                            <span className="reset-password__field-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                    <rect x="4" y="10" width="16" height="10" rx="2" />
                                    <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                                </svg>
                            </span>
                            <CFormInput
                                id="ConfirmPassword"
                                type={confirmVisible ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setPwErrors({ ...pwErrors, confirmPassword: '' });
                                }}
                                required
                            />
                            <button
                                type="button"
                                className="reset-password__field-toggle"
                                onClick={() => setConfirmVisible((p) => !p)}
                            >
                                <img
                                    src={confirmVisible ? openEyeIcon : closedEyeIcon}
                                    alt=""
                                    width="20"
                                />
                            </button>
                        </div>
                        {pwErrors.confirmPassword && (
                            <p className="reset-password__field-error">{pwErrors.confirmPassword}</p>
                        )}

                        <div className="reset-password__requirements">
                            <p className="reset-password__requirements-title">Security Requirements:</p>
                            <ul className="reset-password__requirements-list">
                                <li className={hasMinLength ? 'is-met' : ''}>
                                    <CheckIcon active={hasMinLength} />
                                    At least 8 characters
                                </li>
                                <li className={hasUppercase ? 'is-met' : ''}>
                                    <CheckIcon active={hasUppercase} />
                                    One uppercase letter
                                </li>
                                <li className={hasDigit ? 'is-met' : ''}>
                                    <CheckIcon active={hasDigit} />
                                    One number digit
                                </li>
                                <li className={passwordsMatch ? 'is-met' : ''}>
                                    <CheckIcon active={passwordsMatch} />
                                    Passwords match
                                </li>
                            </ul>
                        </div>

                        <CButton
                            type="submit"
                            className="reset-password__submit"
                            disabled={loading || !allRequirementsMet}
                        >
                            {loading ? <CSpinner size="sm" /> : 'Reset password'}
                        </CButton>

                        <a href="/login" className="reset-password__back">
                            Return to login
                        </a>
                    </CForm>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;